/**
 * Transforms helpful links into category format.
 *
 * @param {Array} data - Helpful Links for each section to transform.
 * @returns {Array} Transformed helpful links in the same format as categories.
 */
const transformHelpfulLinksToCategoryFormat = (data) => data.map((item) => ({
  categoryId: item.categoryId || item.id,
  name: item.name || item.title,
  url: item.url,
  newWindow: item.external || false,
  brand: null,
  subCategories: item.subCategories
    ? transformHelpfulLinksToCategoryFormat(item.subCategories)
    : [],
}));

/**
 * Transforms utility links into a category format.
 *
 * @param {Array} utilityLinks - Utility links to transform.
 * @param {Object} policies - Translated text for "Policies".
 * @returns {Object} Transformed utility links(Policies) in category format.
 */
const transformUtilityLinks = (utilityLinks, policies) => ({
  categoryId: 'privacy',
  name: policies.value,
  subCategories: utilityLinks || [],
});

/**
 * Transforms "About Us" links into a category format.
 *
 * @param {Array} aboutLinks - About Us links to transform.
 * @param {Object} aboutUs - Translated text for "About Us".
 * @returns {Object} Transformed About Us links in category format.
 */
const transformAboutLinks = (aboutLinks, aboutUs) => ({
  categoryId: 'about-us',
  name: aboutUs.value,
  subCategories: aboutLinks || [],
});

/**
 * Transforms help links into a category format.
 *
 * @param {Array} helpLinks - Help links to transform.
 * @param {Object} help - Translated text for "Help".
 * @returns {Object} Transformed help links in category format.
 */
const transformHelpLinks = (helpLinks, help) => ({
  categoryId: 'help-section',
  name: help.value,
  subCategories: helpLinks || [],
});

/**
 * Transforms helpful links data into a category format.
 *
 * @param {Object} helpfulLinksData - Raw helpful links data.
 * @param {Object} translatedTexts - Translated text values for section or subsection headers.
 * i.e., Helpful Links, Policies, About Us, and Help.
 * @returns {Object|null} Transformed helpful links in the same format as all categories,
 * or null if no data is provided.
 */
const getTransformedHelpfulLinks = (helpfulLinksData, translatedTexts) => {
  if (!helpfulLinksData) return null;

  const {
    helpfulLinks, policies, aboutUs, help,
  } = translatedTexts;

  return {
    categoryId: '1',
    name: helpfulLinks.value,
    subCategories: transformHelpfulLinksToCategoryFormat([
      transformUtilityLinks(helpfulLinksData.footer.utilityLinks, policies),
      transformAboutLinks(helpfulLinksData.footer.aboutLinks, aboutUs),
      transformHelpLinks(helpfulLinksData.footer.helpLinks, help),
      ...helpfulLinksData.footer.iconLinks.map((iconLink) => ({
        categoryId: iconLink.id,
        name: iconLink.title,
        subCategories: [],
        url: iconLink.url,
        external: iconLink.external,
      })),
    ]),
    url: null,
    brand: null,
  };
};

export {
  getTransformedHelpfulLinks, transformHelpfulLinksToCategoryFormat,
  transformUtilityLinks, transformAboutLinks, transformHelpLinks,
};
